type JSONSchema = Record<string, unknown>;

const stringSchema = { type: 'string' } satisfies JSONSchema;
const numberSchema = { type: 'number' } satisfies JSONSchema;
const booleanSchema = { type: 'boolean' } satisfies JSONSchema;
const stringArraySchema = { type: 'array', items: stringSchema } satisfies JSONSchema;
const ref = (name: string) => ({ $ref: `#/$defs/${name}` }) satisfies JSONSchema;
const schemaObject = (properties: Record<string, unknown>, options: { additionalProperties?: boolean; required?: string[] } = {}): JSONSchema => ({
  type: 'object',
  additionalProperties: options.additionalProperties ?? false,
  properties,
  ...(options.required?.length ? { required: options.required } : {}),
});
const schemaObjectWithAnyOf = (properties: Record<string, unknown>, anyOfRequired: string[][], options: { additionalProperties?: boolean; required?: string[] } = {}): JSONSchema => ({
  ...schemaObject(properties, options),
  ...(anyOfRequired.length ? { anyOf: anyOfRequired.map((required) => ({ required })) } : {}),
});
const arraySchema = (items: unknown): JSONSchema => ({ type: 'array', items });
const literalSchema = (key: string, value: unknown): JSONSchema => schemaObject({ [key]: value }, { required: [key] });
const arrayInputSchema = (items: unknown): JSONSchema => schemaObjectWithAnyOf({ path: stringSchema, literalArray: arraySchema(items) }, [['path'], ['literalArray']]);

const pathBindingSchema = schemaObject({ path: stringSchema }, { required: ['path'] });
const textBindingSchema = schemaObjectWithAnyOf({ path: stringSchema, literalString: stringSchema }, [['path'], ['literalString']]);
const numberBindingSchema = schemaObjectWithAnyOf({ path: stringSchema, literalNumber: numberSchema }, [['path'], ['literalNumber']]);
const booleanBindingSchema = schemaObjectWithAnyOf({ path: stringSchema, literalBool: booleanSchema, literalBoolean: booleanSchema }, [['path'], ['literalBool'], ['literalBoolean']]);
const stringArrayInputSchema = arrayInputSchema(stringSchema);
const selectionLiteralSchema = literalSchema('literalArray', stringArraySchema);
const actionSelectionBindingSchema = schemaObjectWithAnyOf({ path: stringSchema, literalString: stringSchema, literalArray: stringArraySchema }, [['path'], ['literalString'], ['literalArray']]);
const componentIdsSchema = schemaObject({ explicitList: stringArraySchema }, { required: ['explicitList'] });

const flexibleValueSchema = {
  anyOf: [
    stringSchema,
    numberSchema,
    booleanSchema,
    { type: 'array', items: ref('flexibleValue') },
    schemaObject({
      path: stringSchema,
      valueString: stringSchema,
      valueNumber: numberSchema,
      valueBool: booleanSchema,
      valueBoolean: booleanSchema,
      literalString: stringSchema,
      literalNumber: numberSchema,
      literalBool: booleanSchema,
      literalBoolean: booleanSchema,
      valueArray: { type: 'array', items: ref('flexibleValue') },
      literalArray: { type: 'array', items: ref('flexibleValue') },
      valueMap: { type: 'array', items: ref('dataEntry') },
      literalMap: { type: 'array', items: ref('dataEntry') },
      literalObject: { type: 'object', additionalProperties: true },
    }),
  ],
} satisfies JSONSchema;

const actionContextEntrySchema = schemaObject({ key: stringSchema, value: ref('flexibleValue') }, { required: ['key', 'value'] });
const actionSchema = schemaObject({ name: stringSchema, args: { type: 'object', additionalProperties: true }, label: stringSchema, context: arraySchema(ref('actionContextEntry')) });
const dataEntrySchema = schemaObject(
  {
    key: stringSchema,
    valueString: stringSchema,
    valueNumber: numberSchema,
    valueBool: booleanSchema,
    valueBoolean: booleanSchema,
    literalString: stringSchema,
    literalNumber: numberSchema,
    literalBool: booleanSchema,
    literalBoolean: booleanSchema,
    valueArray: { type: 'array', items: ref('flexibleValue') },
    literalArray: { type: 'array', items: ref('flexibleValue') },
    valueMap: { type: 'array', items: ref('dataEntry') },
    literalMap: { type: 'array', items: ref('dataEntry') },
    literalObject: { type: 'object', additionalProperties: true },
  },
  { required: ['key'] },
);

const selectionItemSchema = schemaObject({ label: stringSchema, title: stringSchema, text: stringSchema, value: stringSchema, child: stringSchema }, { required: ['value', 'child'] });
const selectionWrapItemSchema = schemaObject({ label: stringSchema, title: stringSchema, value: stringSchema, child: stringSchema }, { required: ['value', 'child'] });
const actionSelectionItemSchema = schemaObject({ label: stringSchema, title: stringSchema, value: stringSchema, child: stringSchema, action: stringSchema }, { required: ['value', 'child'] });
const optionItemSchema = schemaObject({ label: stringSchema, title: stringSchema, value: stringSchema });
const bottomBarItemSchema = schemaObject({ title: stringSchema, action: stringSchema });
const tagSegmentSchema = schemaObject({ text: stringSchema, style: stringSchema }, { required: ['text'] });
const tabItemSchema = schemaObject({ title: ref('textBinding'), child: stringSchema }, { required: ['title', 'child'] });
const rollPickerColumnSchema = schemaObject({ title: stringSchema, items: stringArraySchema }, { required: ['title', 'items'] });
const orderedDisplayItemSchema = schemaObject({ child: stringSchema }, { required: ['child'] });
const booleanAllOfOutputSchema = schemaObject({ path: stringSchema, literalBool: booleanSchema, literalBoolean: booleanSchema }, { required: ['path'] });
const booleanAnyOfOutputSchema = schemaObject({ path: stringSchema, literalBool: booleanSchema, literalBoolean: booleanSchema }, { required: ['path'] });
const passwordKeypadValueSchema = schemaObject({ path: stringSchema }, { required: ['path'] });

const LOCAL_A2UI_COMPONENT_SCHEMAS = {
  Column: schemaObject({ children: ref('componentIds'), distribution: stringSchema, alignment: stringSchema }),
  Row: schemaObject({ children: ref('componentIds'), distribution: stringSchema, alignment: stringSchema }),
  Card: schemaObject({ child: stringSchema }),
  Label: schemaObject({ text: ref('textBinding'), variant: stringSchema, color: stringSchema }),
  Button: schemaObject({ child: stringSchema, style: stringSchema, action: ref('action'), enabled: ref('booleanBinding') }),
  Divider: schemaObject({ axis: stringSchema }),
  LinearProgress: schemaObject({ progress: ref('numberBinding') }),
  Rating: schemaObject({ rating: ref('numberBinding'), text: ref('textBinding') }),
  TagText: schemaObject({ segments: arrayInputSchema(ref('tagSegment')) }, { required: ['segments'] }),
  FilterTags: schemaObject({ tags: ref('stringArrayInput') }, { required: ['tags'] }),
  Icon: schemaObject({ name: ref('textBinding'), style: stringSchema }),
  Image: schemaObject({ url: ref('textBinding'), size: stringSchema }),
  CircularProgress: schemaObject({ value: ref('numberBinding'), max: ref('numberBinding'), style: stringSchema, iconName: ref('textBinding'), iconStyle: stringSchema }),
  Tabs: schemaObject({ tabItems: arraySchema(ref('tabItem')) }, { required: ['tabItems'] }),
  Carousel: schemaObject({ children: ref('componentIds') }),
  BottomBar: schemaObject({ child: stringSchema }, { required: ['child'] }),
  DateTimeInput: schemaObject({ value: ref('textBinding'), enableDate: booleanSchema, enableTime: booleanSchema, firstDate: stringSchema, lastDate: stringSchema }, { required: ['value'] }),
  DropdownSelection: schemaObject({ selection: ref('stringArrayInput'), items: stringArraySchema, placeholder: ref('textBinding'), hasSelection: ref('booleanBinding') }, { required: ['selection', 'items'] }),
  MarkdownView: schemaObject({ text: ref('textBinding') }, { required: ['text'] }),
  Map: schemaObject({ latitude: ref('numberBinding'), longitude: ref('numberBinding'), zoom: ref('numberBinding'), tileUrlTemplate: ref('textBinding') }, { required: ['latitude', 'longitude'] }),
  PasswordKeypad: schemaObject({ value: ref('passwordKeypadValue'), action: ref('action') }, { required: ['value', 'action'] }),
  PhotoInput: schemaObject({ value: ref('textBinding'), hasValue: ref('booleanBinding'), isUploading: ref('booleanBinding'), placeholder: ref('textBinding') }, { required: ['value'] }),
  RollPicker: schemaObject({ columns: arraySchema(ref('rollPickerColumn')), selection: ref('stringArrayInput'), hasCustomSelection: ref('booleanBinding') }, { required: ['columns', 'selection'] }),
  RollPickerCard: schemaObject(
    {
      columns: arraySchema(ref('rollPickerColumn')),
      selection: ref('stringArrayInput'),
      action: ref('action'),
      hasCustomSelection: ref('booleanBinding'),
      title: ref('textBinding'),
      subTitle: ref('textBinding'),
      submitText: ref('textBinding'),
      submittedText: ref('textBinding'),
    },
    { required: ['columns', 'selection', 'action'] },
  ),
  SelectionList: schemaObject(
    { selection: ref('stringArrayInput'), maxSelection: numberSchema, requiredSelection: numberSchema, hasSelection: ref('booleanBinding'), items: arraySchema(ref('selectionItem')) },
    { required: ['selection', 'items'] },
  ),
  SelectionGrid: schemaObject(
    { selection: ref('stringArrayInput'), maxSelection: numberSchema, requiredSelection: numberSchema, hasSelection: ref('booleanBinding'), items: arraySchema(ref('selectionItem')) },
    { required: ['selection', 'items'] },
  ),
  SelectionWrap: schemaObject(
    { selection: ref('stringArrayInput'), maxSelection: numberSchema, requiredSelection: numberSchema, hasSelection: ref('booleanBinding'), items: arraySchema(ref('selectionWrapItem')) },
    { required: ['selection', 'items'] },
  ),
  OrderedSelectionList: schemaObject(
    { selection: ref('stringArrayInput'), maxSelection: numberSchema, requiredSelection: numberSchema, hasSelection: ref('booleanBinding'), items: arraySchema(ref('selectionItem')) },
    { required: ['selection', 'items'] },
  ),
  OrderedDisplayList: schemaObject({ items: arraySchema(ref('orderedDisplayItem')) }, { required: ['items'] }),
  ActionSelectionList: schemaObject({ selection: ref('stringArrayInput'), action: ref('action'), items: arraySchema(ref('actionSelectionItem')) }, { required: ['selection', 'action', 'items'] }),
  BooleanAllOf: schemaObject({ inputs: arraySchema(ref('booleanBinding')), output: ref('booleanAllOfOutput') }, { required: ['inputs', 'output'] }),
  BooleanAnyOf: schemaObject({ inputs: arraySchema(ref('booleanBinding')), output: ref('booleanAnyOfOutput') }, { required: ['inputs', 'output'] }),
  TickSlider: schemaObject({ value: ref('numberBinding'), max: ref('numberBinding') }, { required: ['value', 'max'] }),
  FullScreenModal: schemaObject({ entryPointChild: stringSchema, contentChild: stringSchema }, { required: ['entryPointChild', 'contentChild'] }),
} as const satisfies Record<string, JSONSchema>;

const localComponentSchemas = Object.entries(LOCAL_A2UI_COMPONENT_SCHEMAS).map(([type, schema]) => schemaObject({ [type]: schema }, { required: [type] }));
const surfaceComponentSchema = schemaObject({ id: stringSchema, weight: numberSchema, component: { oneOf: localComponentSchemas } }, { required: ['id', 'component'] });
const serverMessageSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    beginRendering: schemaObject({ surfaceId: stringSchema, catalogId: stringSchema, root: stringSchema, styles: { type: 'object', additionalProperties: true } }, { required: ['surfaceId', 'root'], additionalProperties: true }),
    surfaceUpdate: schemaObject({ surfaceId: stringSchema, components: { type: 'array', items: surfaceComponentSchema } }, { required: ['surfaceId', 'components'], additionalProperties: true }),
    dataModelUpdate: schemaObject({ surfaceId: stringSchema, path: stringSchema, contents: { type: 'array', items: ref('dataEntry') } }, { required: ['surfaceId', 'contents'], additionalProperties: true }),
    deleteSurface: schemaObject({ surfaceId: stringSchema }, { required: ['surfaceId'], additionalProperties: true }),
  },
  oneOf: [{ required: ['beginRendering'] }, { required: ['surfaceUpdate'] }, { required: ['dataModelUpdate'] }, { required: ['deleteSurface'] }],
} satisfies JSONSchema;
const messageArraySchema = { type: 'array', items: ref('serverMessage') } satisfies JSONSchema;
const payloadSchema = schemaObject({ objects: messageArraySchema, messages: messageArraySchema }, { additionalProperties: true });
const canvasSurfaceSchema = schemaObject(
  {
    generation_model: stringSchema,
    is_interactable: booleanSchema,
    payload: payloadSchema,
    protocol_version: stringSchema,
    schema_version: stringSchema,
    summary: stringSchema,
    surface_id: stringSchema,
    surface_status: stringSchema,
    surface_version: numberSchema,
    messages: messageArraySchema,
    objects: messageArraySchema,
    a2ui_messages: messageArraySchema,
  },
  { additionalProperties: true },
);

export const LOCAL_A2UI_PREVIEW_RESOLVED_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Macaron Web A2UI Preview Schema',
  $defs: {
    pathBinding: pathBindingSchema,
    textBinding: textBindingSchema,
    numberBinding: numberBindingSchema,
    booleanBinding: booleanBindingSchema,
    action: actionSchema,
    actionContextEntry: actionContextEntrySchema,
    actionSelectionBinding: actionSelectionBindingSchema,
    dataEntry: dataEntrySchema,
    flexibleValue: flexibleValueSchema,
    componentIds: componentIdsSchema,
    stringArrayInput: stringArrayInputSchema,
    selectionLiteral: selectionLiteralSchema,
    selectionItem: selectionItemSchema,
    selectionItemArrayInput: arrayInputSchema(ref('selectionItem')),
    selectionWrapItem: selectionWrapItemSchema,
    selectionWrapItemArrayInput: arrayInputSchema(ref('selectionWrapItem')),
    actionSelectionItem: actionSelectionItemSchema,
    actionSelectionItemArrayInput: arrayInputSchema(ref('actionSelectionItem')),
    optionItem: optionItemSchema,
    optionItemArrayInput: arrayInputSchema(ref('optionItem')),
    bottomBarItem: bottomBarItemSchema,
    bottomBarItemArrayInput: arrayInputSchema(ref('bottomBarItem')),
    tagSegment: tagSegmentSchema,
    tabItem: tabItemSchema,
    tabItemArrayInput: arrayInputSchema(ref('tabItem')),
    rollPickerColumn: rollPickerColumnSchema,
    rollPickerColumnArrayInput: arrayInputSchema(ref('rollPickerColumn')),
    orderedDisplayItem: orderedDisplayItemSchema,
    booleanAllOfOutput: booleanAllOfOutputSchema,
    booleanAnyOfOutput: booleanAnyOfOutputSchema,
    passwordKeypadValue: passwordKeypadValueSchema,
    serverMessage: serverMessageSchema,
  },
  anyOf: [
    messageArraySchema,
    {
      type: 'object',
      additionalProperties: true,
      properties: {
        canvas_surface: canvasSurfaceSchema,
        surface_id: stringSchema,
        surface_version: numberSchema,
        origin_message_id: stringSchema,
        payload: payloadSchema,
        messages: messageArraySchema,
        objects: messageArraySchema,
        a2ui_messages: messageArraySchema,
        protocol_version: stringSchema,
        schema_version: stringSchema,
        summary: stringSchema,
        surface_status: stringSchema,
        generation_model: stringSchema,
        is_interactable: booleanSchema,
      },
      anyOf: [{ required: ['canvas_surface'] }, { required: ['payload'] }, { required: ['messages'] }, { required: ['objects'] }, { required: ['a2ui_messages'] }],
    },
  ],
} satisfies JSONSchema;

export const LOCAL_A2UI_PREVIEW_MONACO_JSON_SCHEMA = {
  uri: 'https://macaron.local/a2ui-preview/local_debug_resolved_schema.json',
  fileMatch: ['preview.json', '**/preview.json'],
  schema: LOCAL_A2UI_PREVIEW_RESOLVED_SCHEMA,
};
